import type { Components } from '@mildang/mui';
import type { Theme, PaletteColor } from '@mildang/mui';

const buildContainedButton = (paletteColor: PaletteColor) => ({
  '&:hover': {
    backgroundColor: paletteColor.darkHover,
  },
  '&:active': {
    backgroundColor: paletteColor.darkActive,
  },
  '&.Mui-disabled': {
    backgroundColor: paletteColor.main,
    color: paletteColor.contrastText,
    opacity: 0.38,
  },
});

const createButtons = ({ palette, typography }: Theme): Components => ({
  MuiButton: {
    defaultProps: {
      disableElevation: true,
    },
    variants: [
      {
        props: { size: 'xLarge' },
        style: {
          padding: '11px 24px',
          letterSpacing: '0.0132em',
          ...typography.buttonXL,
          '& .MuiButton-iconSizeXLarge > *:nth-of-type(1)': {
            fontSize: 24,
          },
          '& .MuiButton-iconSizeXLarge.MuiButton-startIcon': {
            marginRight: 8,
          },
          '& .MuiButton-iconSizeXLarge.MuiButton-endIcon': {
            marginLeft: 8,
          },
        },
      },
      {
        props: { size: 'xLarge', variant: 'outlined' },
        style: {
          padding: '10px 23px',
        },
      },
      {
        props: { size: 'xLarge', variant: 'text' },
        style: {
          padding: '11px 12px',
        },
      },
    ],
    styleOverrides: {
      root: {
        minWidth: 30,
        borderRadius: 8,
      },

      sizeLarge: {
        padding: '8px 20px',
        letterSpacing: '0.0132em',
        ...typography.buttonL,
        '& .MuiButton-iconSizeLarge': {
          '& > *:nth-of-type(1)': {
            fontSize: 22,
          },
          '&.MuiButton-startIcon': {
            marginRight: 6,
          },
          '&.MuiButton-endIcon': {
            marginLeft: 6,
          },
        },
      },
      sizeMedium: {
        letterSpacing: '0.0085em',
        padding: '6px 16px',
        ...typography.buttonM,
        '& .MuiButton-iconSizeMedium': {
          '&.MuiButton-startIcon': {
            marginRight: 4,
          },
          '&.MuiButton-endIcon': {
            marginLeft: 4,
          },
        },
      },
      sizeSmall: {
        padding: '6px 12px',
        ...typography.buttonS,
        '& .MuiButton-iconSizeSmall': {
          '&.MuiButton-startIcon': {
            marginLeft: -4,
            marginRight: 4,
          },
          '&.MuiButton-endIcon': {
            marginRight: -4,
            marginLeft: 4,
          },
        },
      },
      containedPrimary: buildContainedButton(palette.primary),
      containedSecondary: buildContainedButton(palette.secondary),
      // TODO: type me
      // @ts-ignore
      containedError: buildContainedButton(palette.error),
      outlinedSizeLarge: {
        padding: '7px 19px',
      },
      outlinedSizeMedium: {
        padding: '5px 15px',
      },
      outlinedSizeSmall: {
        padding: '5px 11px',
      },
      outlinedPrimary: {
        color: palette.text.tertiary,
        backgroundColor: palette.primary.light,
        borderColor: palette.primary.stroke,
        '&:hover': {
          backgroundColor: palette.primary.lightHover,
          borderColor: palette.primary.strokeHover,
        },
        '&:active': {
          backgroundColor: palette.primary.lightActive,
        },
        '&.Mui-disabled': {
          borderColor: palette.primary.stroke,
          backgroundColor: palette.primary.light,
          opacity: 0.38,
        },
      },
      outlinedSecondary: {
        color: palette.secondary.dark,
        borderColor: palette.secondary.stroke,
        '&:hover': {
          backgroundColor: palette.secondary.light,
          borderColor: palette.secondary.strokeHover,
        },
        '&:active': {
          backgroundColor: palette.secondary.lightActive,
        },
        '&.Mui-disabled': {
          color: palette.secondary.dark,
          backgroundColor: palette.secondary.light,
          borderColor: palette.secondary.stroke,

          opacity: 0.38,
        },
      },
      // @ts-ignore
      outlinedError: {
        color: palette.error.dark,
        borderColor: palette.error.stroke,
        '&:hover': {
          borderColor: palette.error.strokeHover,
          backgroundColor: palette.error.lightHover,
        },
        '&:active': {
          borderColor: palette.error.strokeHover,
          backgroundColor: palette.error.lightActive,
        },
        '&.Mui-disabled': {
          color: palette.error.dark,
          borderColor: palette.error.stroke,
          opacity: 0.38,
        },
      },
      textSizeLarge: {
        padding: '8px 10px',
      },
      textSizeMedium: {
        padding: '6px 8px',
      },
      textSizeSmall: {
        padding: '6px 6px',
      },
      textPrimary: {
        color: palette.text.tertiary,
        '&:hover': {
          backgroundColor: palette.primary.lightHover,
        },
        '&:active': {
          backgroundColor: palette.primary.light,
        },
        '&.Mui-disabled': {
          opacity: 0.38,
        },
      },
      textSecondary: {
        color: palette.secondary.dark,
        '&:hover': {
          backgroundColor: palette.secondary.light,
        },
        '&:active': {
          backgroundColor: palette.secondary.light,
        },
        '&.Mui-disabled': {
          color: palette.secondary.dark,
          opacity: 0.38,
        },
      },
    },
  },
});

export default createButtons;
